import { Modal } from "antd";
import { useApplicationError } from "../../hooks/useApplicationError";
import { useMemo } from "react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ProblemDetails } from "../../redux/apiSpecifications/apiAppraisalcom";
import { useTranslation } from "react-i18next";
import { ModalButtons, ModalContent } from "../../style/Modal";
import { ButtonOkError } from "./Buttons";
import styled from "styled-components";
import { IconError } from "../../config/icons";
import { ApplicationErrorType } from "../../redux/features/applicationErrorSlice";
import { components } from "../../config/testIds";

export const ModalBody = styled.div`
  div.icon {
    text-align: center;
    color: #ff4d4f;
  }

  h2 {
    text-align: center;
    color: #ff4d4f;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }
`;

const isFetchBaseQueryError = (error: any): error is FetchBaseQueryError =>
  (error as FetchBaseQueryError).status !== undefined;

const ApplicationErrorsModal = () => {
  const { t } = useTranslation();

  const { error, hideApplicationError } = useApplicationError();

  const handleCloseModal = () => {
    hideApplicationError();
  };

  return (
    <>
      {error && (
        <Modal
          open={true}
          footer={null}
          closable={false}
          destroyOnHidden={true}
          data-testid={components.layout.applicationErrorsModal.modal}
        >
          <ModalContent>
            <ModalBody>
              <div className="icon">
                <IconError size={36} />
              </div>

              <h2>{t("application-errors-modal-attention-label")}</h2>

              <ApplicationError error={error} />
            </ModalBody>

            <ModalButtons>
              <ButtonOkError onClick={handleCloseModal}>Ok</ButtonOkError>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ApplicationErrorsModal;

interface IApplicationErrorAlertProps {
  error: ApplicationErrorType;
}

export const ApplicationErrorAlert = ({
  error,
}: IApplicationErrorAlertProps) => {
  return (
    <div
      data-show="true"
      className="ant-alert ant-alert-error ant-alert-no-icon"
      role="alert"
    >
      <div className="ant-alert-content">
        <div className="ant-alert-message">
          <ApplicationError error={error} />
        </div>
      </div>
    </div>
  );
};

interface IApplicationErrorProps {
  error: ApplicationErrorType;
}

const ApplicationError = ({ error }: IApplicationErrorProps) => {
  const { t } = useTranslation();

  const errorMessages: string[] = useMemo(() => {
    if (typeof error === "string") {
      return [error];
    } else if (isFetchBaseQueryError(error)) {
      const data = error.data! as ProblemDetails;

      if (data.applicationErrors && data.applicationErrors.length > 0) {
        return data.applicationErrors.map((e) => {
          if (e.errorCode === "COM-XXX-016") {
            const entity = e.errorDetails![0].value!;

            return t(`MESSAGES.${entity}-not-found`);
          } else {
            const options = e.errorDetails?.reduce(
              (acc, o) => ({
                ...acc,
                [o.key!]: o.key === "field" ? t(`LABELS.${o.value}`) : o.value,
              }),
              {}
            );

            return t(`MESSAGES.${e.errorCode}`, options!);
          }
        });
      } else return [data.title!];
    } else {
      return ["Error"];
    }
  }, [error]);

  return (
    <div data-testid={components.layout.applicationError.container}>
      {errorMessages.length === 1 ? (
        errorMessages[0]
      ) : (
        <ul>
          {errorMessages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
